// Block-2-Karte „Courté Intern" (AP-N, DD4): die neuesten redaktionellen News
// (Text + Bilder) aus `announcementService.listMine()`. Ungelesene News werden
// hervorgehoben (Punkt + fett); ein Klick expandiert die Detailansicht und
// markiert die News als gelesen (`markRead` → Indikator aktualisiert).
//
// Hinweis Bilder: die `images`-Refs (`/media/news/<file>`) sind nur über den
// authentifizierten `/media`-Proxy abrufbar. Ein nacktes `<img src>` trägt den
// Bearer-Token NICHT (der wird nur in `fetch` injiziert), darum laden wir das
// Bild über das gewrappte `fetch` und rendern eine kurzlebige blob:-URL.
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Megaphone20Regular } from "@fluentui/react-icons";
import {
    announcementService,
    type AnnouncementForUser,
} from "../../services/announcementService";
import { apiUrl } from "../../utils/api";
import { REFINED } from "../risikoanalyseStep/tokens";
import { DashCard, EmptyHint, deDate } from "./DashboardShared";

const LIMIT = 4;

export const CourteInternCard: React.FC = () => {
    const [items, setItems] = useState<AnnouncementForUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        announcementService
            .listMine()
            .then((res) => {
                if (cancelled) return;
                setItems(res.slice(0, LIMIT));
            })
            .catch(() => {
                if (!cancelled) setItems([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);

    const handleClick = (n: AnnouncementForUser) => {
        // Detail ein-/ausklappen.
        setOpenId((cur) => (cur === n.id ? null : n.id));
        // Beim Öffnen einer ungelesenen News: gelesen markieren + Indikator updaten.
        if (!n.read) {
            setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
            announcementService.markRead(n.id).catch(() => {
                // Bei Fehler den ungelesen-Status zurückrollen, damit der Indikator stimmt.
                setItems((cur) => cur.map((x) => (x.id === n.id ? { ...x, read: false } : x)));
            });
        }
    };

    return (
        <DashCard
            title="Courté Intern"
            icon={<Megaphone20Regular />}
            right={
                unreadCount > 0 ? (
                    <span
                        style={{
                            minWidth: 22,
                            height: 22,
                            padding: "0 7px",
                            borderRadius: 999,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.78rem",
                            fontWeight: 800,
                            backgroundColor: REFINED.aubDeep,
                            color: REFINED.white,
                        }}
                        title={`${unreadCount} ungelesen`}
                    >
                        {unreadCount}
                    </span>
                ) : undefined
            }
        >
            {loading ? (
                <EmptyHint>Lade Neuigkeiten…</EmptyHint>
            ) : items.length === 0 ? (
                <EmptyHint>Keine Neuigkeiten.</EmptyHint>
            ) : (
                <div>
                    {items.map((n, idx) => (
                        <NewsRow
                            key={n.id}
                            news={n}
                            border={idx > 0}
                            expanded={openId === n.id}
                            onClick={() => handleClick(n)}
                        />
                    ))}
                </div>
            )}
        </DashCard>
    );
};

/* ────────── Eine News-Zeile (Thumbnail + Titel/Datum + gelesen-Indikator) ────────── */

const NewsRow: React.FC<{
    news: AnnouncementForUser;
    border: boolean;
    expanded: boolean;
    onClick: () => void;
}> = ({ news, border, expanded, onClick }) => {
    const firstImage = news.images && news.images.length > 0 ? news.images[0] : null;
    const thumb = useMediaBlob(firstImage);

    return (
        <div
            style={{
                borderTop: border ? `1px solid ${REFINED.line}` : "none",
                // Ungelesene News dezent hervorheben (heller Aubergine-Hintergrund).
                backgroundColor: news.read ? "transparent" : REFINED.aub50,
                borderRadius: news.read ? 0 : 10,
                margin: news.read ? 0 : "4px 0",
            }}
        >
            <div
                onClick={onClick}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onClick();
                    }
                }}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 8px",
                    cursor: "pointer",
                }}
            >
                {/* gelesen/ungelesen-Indikator: gefüllter Punkt für ungelesen */}
                <span
                    style={{
                        width: 9,
                        height: 9,
                        borderRadius: 999,
                        flexShrink: 0,
                        backgroundColor: news.read ? "transparent" : REFINED.aub,
                        border: news.read ? `1px solid ${REFINED.line}` : "none",
                    }}
                    aria-hidden
                />

                {thumb && (
                    <img
                        src={thumb}
                        alt=""
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            objectFit: "cover",
                            flexShrink: 0,
                            border: `1px solid ${REFINED.line}`,
                        }}
                    />
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                        style={{
                            fontWeight: news.read ? 600 : 800,
                            fontSize: "0.95rem",
                            color: REFINED.ink,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {news.title}
                    </div>
                    {!expanded && (
                        <div
                            style={{
                                fontSize: "0.83rem",
                                color: REFINED.ink2,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {news.body}
                        </div>
                    )}
                </div>

                <span
                    style={{
                        flexShrink: 0,
                        fontVariantNumeric: "tabular-nums",
                        color: REFINED.ink3,
                        fontWeight: 700,
                        fontSize: "0.82rem",
                    }}
                >
                    {deDate(news.createdAt)}
                </span>
            </div>

            {/* Detail/Expand: voller Text + alle Bilder */}
            {expanded && (
                <div style={{ padding: "0 8px 14px 30px" }}>
                    <div
                        style={{
                            fontSize: "0.9rem",
                            color: REFINED.ink2,
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {news.body}
                    </div>
                    {news.images && news.images.length > 0 && (
                        <NewsImages refs={news.images} />
                    )}
                </div>
            )}
        </div>
    );
};

/* ────────── Bild-Galerie der Detailansicht ────────── */

const NewsImages: React.FC<{ refs: string[] }> = ({ refs }) => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
        {refs.map((ref) => (
            <NewsImage key={ref} mediaRef={ref} />
        ))}
    </div>
);

const NewsImage: React.FC<{ mediaRef: string }> = ({ mediaRef }) => {
    const url = useMediaBlob(mediaRef);
    if (!url) return null;
    return (
        <img
            src={url}
            alt=""
            style={{
                maxWidth: "100%",
                maxHeight: 220,
                borderRadius: 10,
                border: `1px solid ${REFINED.line}`,
                objectFit: "cover",
            }}
        />
    );
};

/* ────────── Hilfs-Hook: authentifizierten /media-Ref als blob:-URL laden ──────────
 * Der `/media`-Proxy verlangt den Bearer-Token, den nur `fetch` (über den
 * api.ts-Wrapper) trägt — ein nacktes `<img src>` bekäme 401. Daher die Bytes
 * holen, als blob:-URL bereitstellen und beim Unmount/Wechsel wieder freigeben. */
function useMediaBlob(mediaRef: string | null): string | null {
    const [url, setUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!mediaRef) {
            setUrl(null);
            return;
        }
        let objectUrl: string | null = null;
        let cancelled = false;
        // Ref wie "/media/news/<file>" → api-relativ; apiUrl hängt /api an und der
        // fetch-Wrapper ergänzt den Bearer-Token.
        fetch(apiUrl(mediaRef.replace(/^\/+/, "")))
            .then((res) => (res.ok ? res.blob() : Promise.reject(res.status)))
            .then((blob) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setUrl(objectUrl);
            })
            .catch(() => {
                if (!cancelled) setUrl(null);
            });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [mediaRef]);

    return url;
}

export default CourteInternCard;
